import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  AlertCircle,
  Plus,
  Eye,
  Trash2,
  Image as ImageIcon,
  Edit,
} from "lucide-react";
import Swal from "sweetalert2";
import ViewBlogModal from "../../components/AdminComponents/blog/ViewBlogModal";
import UpdateBlogModal from "../../components/AdminComponents/blog/UpdateBlogModal";
import CreateBlogModal from "../../components/AdminComponents/blog/CreateBlogModal";
import BlogsSkeleton from "../../components/AdminComponents/blog/BlogsSkeleton";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [viewBlogModal, setViewBlogModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [createBlogModal, setCreateBlogModal] = useState(false);
  const [updateBlogModal, setUpdateBlogModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blog/get-all-blogs`,
        // { withCredentials: true }
      );

      if (response.data.success) {
        setBlogs(response.data.data);
        setFilteredBlogs(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError(err.response?.data?.message || "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = () => {
    setCreateBlogModal(true);
  };

  const handleViewBlog = (blog) => {
    setSelectedBlog(blog);
    setViewBlogModal(true);
  };

  const handleUpdateBlog = (blog) => {
    setSelectedBlog(blog);
    setUpdateBlogModal(true);
  };

  const handleDeleteBlog = async (blog) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This will delete the blog permanently!",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
    });

    if (confirm.isConfirmed) {
      try {
        setDeleteLoading(blog._id);
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/blog/delete-blog/${blog._id}`,
          // { withCredentials: true }
        );

        // Update both blogs arrays
        const updatedBlogs = blogs.filter((b) => b._id !== blog._id);
        setBlogs(updatedBlogs);
        setFilteredBlogs(updatedBlogs);

        // Adjust current page if needed
        if (currentBlogs.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }

        Swal.fire("Deleted!", "Blog has been deleted.", "success");
      } catch (err) {
        console.error("Error deleting blog:", err);
        Swal.fire("Error!", err.response?.data?.message || "Failed to delete blog", "error");
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  // Pagination calculations
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Error Loading Blogs
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchBlogs}
            className="bg-[#0bb6d9] text-white px-6 py-2 rounded-lg hover:bg-[#069ab7]"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 ">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-gray-800">
              <span className="text-[#0bb6d9]">Blogs</span>
            </h1>
            <p className="text-gray-600 mt-2">
              Showing {currentBlogs.length} of {filteredBlogs.length} blogs
            </p>
          </div>

          <button
            onClick={handleCreateBlog}
            className="flex items-center gap-2 bg-[#0bb6d9] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#069ab7] shadow-lg"
          >
            <Plus className="w-5 h-5" />
            Create Blog
          </button>
        </div>

        {
          loading ? <BlogsSkeleton /> :
            <>
              {currentBlogs.length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-white rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                    No Blogs Available
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Create your first blog to get started
                  </p>
                  <button
                    onClick={handleCreateBlog}
                    className="inline-flex items-center gap-2 bg-[#0bb6d9] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#069ab7]"
                  >
                    <Plus className="w-5 h-5" />
                    Create First Blog
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentBlogs.map((blog) => (
                      <div
                        key={blog._id}
                        className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow group duration-500 overflow-hidden"
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={blog.image}
                            alt={blog.title}
                            className="w-full h-full object-cover hover:scale-110 transition duration-300"
                            onError={(e) =>
                            (e.target.src =
                              "https://via.placeholder.com/400x300?text=Image+Not+Found")
                            }
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        </div>

                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                            {blog.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                            {blog.description}
                          </p>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewBlog(blog)}
                              className="flex-1 bg-[#0bb6d9] text-white py-2 gap-2 rounded-lg hover:bg-[#069ab7] flex items-center justify-center"
                            >
                              <Eye className="w-4 h-4 md:flex hidden" />
                              View
                            </button>
                            <button
                              onClick={() => handleUpdateBlog(blog)}
                              className="flex-1 bg-blue-500 text-white py-2 gap-2 rounded-lg hover:bg-blue-600 flex items-center justify-center"
                            >
                              <Edit className="w-4 h-4  md:flex hidden" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteBlog(blog)}
                              disabled={deleteLoading === blog._id}
                              className="flex-1 bg-red-500 gap-2  text-white py-2 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center justify-center"
                            >
                              {deleteLoading === blog._id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4  md:flex hidden" />
                              )}
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
        }

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}

        {viewBlogModal && (
          <ViewBlogModal
            blog={selectedBlog}
            onClose={() => setViewBlogModal(false)}
            onClick={() => {
              setUpdateBlogModal(true);
              setViewBlogModal(false);
            }}
          />
        )}

        {updateBlogModal && (
          <UpdateBlogModal
            blog={selectedBlog}
            onClose={() => setUpdateBlogModal(false)}
            onSuccess={fetchBlogs}
          />
        )}

        {createBlogModal && (
          <CreateBlogModal
            onClose={() => setCreateBlogModal(false)}
            onSuccess={fetchBlogs}
          />
        )}
      </div>
    </div>
  );
};

export default AdminBlog;